import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Flask } from "@phosphor-icons/react";
import { getSystemFlags } from "../lib/api";
import { BlockingMessageDialog } from "./BlockingMessageDialog";

/**
 * v0.5.2 — persistent banner shown whenever the backend is running in
 * VIES stub mode, so operators can never mistake simulated VAT-number
 * results for real VIES/HMRC checks.
 *
 * v0.6.8 — the thin top strip stays (it needs to remain visible for
 * the WHOLE session, since it affects every VAT-number check, not one
 * action), but a real compliance-risk disclosure this significant
 * shouldn't rely on a 1-line strip being noticed at a glance. Added a
 * one-time popup the first time stub mode is detected each session —
 * dismissible, doesn't repeat on every render, while the strip keeps
 * serving as the ongoing reminder afterward.
 */
export const ViesStubBanner = () => {
  const { t } = useTranslation();
  const [on, setOn] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const f = await getSystemFlags();
        if (!cancelled && f.vies_stub_mode) {
          setOn(true);
          setShowDialog(true);
        }
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!on) return null;
  return (
    <>
      <BlockingMessageDialog
        open={showDialog} testid="vies-stub-dialog" severity="warning"
        title={t("ui.header.vies_stub.dialog_title", { defaultValue: "VIES stub mode is active" })}
        onOpenChange={(o) => !o && setShowDialog(false)}
      >
        <p>
          {t("ui.header.vies_stub.dialog_body", {
            defaultValue: "This backend is running in VIES stub mode — every buyer VAT-number check returns a SIMULATED result, not a real VIES/HMRC check. Do not rely on these results for an actual filing.",
          })}
        </p>
      </BlockingMessageDialog>
      <div
        data-testid="vies-stub-banner"
        className="w-full bg-fuchsia-600 text-white tabular-nums text-[11px] tracking-[0.15em] uppercase text-center py-1 flex items-center justify-center gap-2 z-50"
      >
        <Flask size={13} weight="fill" />
        {t("ui.header.vies_stub.strip", { defaultValue: "VIES stub mode — simulated results, not real VIES/HMRC checks" })}
      </div>
    </>
  );
};

export default ViesStubBanner;
